/**
 * TeamSettingsModal Component
 *
 * Team settings, opened from a team card (the standalone team-details page was removed).
 * Handles:
 * - Name / description edit
 * - Members management (add / remove, with task unassignment)
 * - Danger Zone delete (soft-delete + disconnect all relations)
 *
 * Task data comes from the team's own `tasks` (already selected by the TEAMS query), so this
 * modal needs no separate task fetch. Duplicate-name validation uses the `allTeams` prop.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Input,
  Select,
  Divider,
  Table,
  Button,
  Popconfirm,
  Tooltip,
  message,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  UserDeleteOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { space, radius, text } from '../../configs/designTokens';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';
import '../../styles/index.scss';

const { Option } = Select;

const TeamSettingsModal = ({ team, visible, onClose, onChanged, allTeams }) => {
  const { updateTeam, updateTeamLoading, updateTask } = useTaskContext();
  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();

  const [settingsForm] = Form.useForm();
  const [editingTeam, setEditingTeam] = useState(null);

  const [isAddMemberModalVisible, setIsAddMemberModalVisible] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const teamId = team?.id;
  const teamTasks = team?.tasks || [];

  // Fetch task-management users for the team's org (members picker)
  const fetchUsersForOrg = (name = '') => {
    const orgId = team?.org?.id;
    if (!orgId) return;
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: orgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
  };

  // Initialise (and reset transient state) when the modal opens for a team
  useEffect(() => {
    if (visible && team) {
      settingsForm.setFieldsValue({
        name: team.name,
        description: team.description || '',
      });
      setEditingTeam({ ...team });
      fetchUsersForOrg();
    }
    if (!visible) {
      setEditingTeam(null);
      setIsAddMemberModalVisible(false);
      setSelectedMemberIds([]);
      setIsDeleteModalVisible(false);
      setDeleteConfirmText('');
      settingsForm.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, teamId]);

  const handleSettingsSave = async () => {
    try {
      const values = await settingsForm.validateFields();
      await updateTeam({
        variables: {
          data: { name: values.name, description: values.description },
          where: { id: teamId },
        },
      });
      message.success('Team updated successfully');
      if (onChanged) onChanged();
      onClose();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to update team: ' + error.message);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      // Step 1: Unassign user from any team tasks where they're an assignee
      const tasksToUpdate = teamTasks.filter((task) => {
        const inMulti = task.assigneeUsers?.some((u) => u.id === userId);
        const isSingle = task.assigneeUser?.id === userId;
        return inMulti || isSingle;
      });
      for (const task of tasksToUpdate) {
        const taskData = {};
        if (task.assigneeUsers?.some((u) => u.id === userId)) {
          taskData.assigneeUsers = { disconnect: [{ id: userId }] };
        }
        if (task.assigneeUser?.id === userId) {
          taskData.assigneeUser = { disconnect: true };
        }
        await updateTask({ variables: { data: taskData, where: { id: task.id } } });
      }

      // Step 2: Remove user from team members
      await updateTeam({
        variables: {
          data: { members: { disconnect: [{ id: userId }] } },
          where: { id: teamId },
        },
      });

      const unassignedCount = tasksToUpdate.length;
      message.success(
        unassignedCount > 0
          ? `Member removed and unassigned from ${unassignedCount} task${unassignedCount === 1 ? '' : 's'}`
          : 'Member removed'
      );
      if (onChanged) onChanged();
      setEditingTeam((prev) => ({
        ...prev,
        members: (prev?.members || []).filter((m) => m.id !== userId),
      }));
    } catch (error) {
      message.error('Failed to remove member: ' + error.message);
    }
  };

  const handleAddMembers = async () => {
    if (selectedMemberIds.length === 0) {
      message.warning('Please select at least one member to add');
      return;
    }
    try {
      await updateTeam({
        variables: {
          data: { members: { connect: selectedMemberIds.map((id) => ({ id })) } },
          where: { id: teamId },
        },
      });
      const count = selectedMemberIds.length;
      message.success(`${count} member${count > 1 ? 's' : ''} added to team`);
      if (onChanged) onChanged();
      const addedUsers = basicUsersDetails.filter((u) => selectedMemberIds.includes(u.id));
      setEditingTeam((prev) => ({
        ...prev,
        members: [...(prev?.members || []), ...addedUsers],
      }));
      setIsAddMemberModalVisible(false);
      setSelectedMemberIds([]);
    } catch (error) {
      message.error('Failed to add members: ' + error.message);
    }
  };

  const handleDeleteTeam = async () => {
    if (!team || deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    try {
      // Step 1: Clear assignees from each linked task sequentially
      const tasksToUpdate = teamTasks.filter(
        (task) => task.assigneeUsers?.length > 0 || task.assigneeUser?.id
      );
      for (const task of tasksToUpdate) {
        const taskData = {};
        if (task.assigneeUsers?.length > 0) {
          taskData.assigneeUsers = {
            disconnect: task.assigneeUsers.map((u) => ({ id: u.id })),
          };
        }
        if (task.assigneeUser?.id) {
          taskData.assigneeUser = { disconnect: true };
        }
        await updateTask({ variables: { data: taskData, where: { id: task.id } } });
      }

      // Step 2: Soft-delete (rename to __deleted__) and disconnect all relations
      const teamData = { name: `__deleted__${team.name}` };
      if (team.tasks?.length > 0) {
        teamData.tasks = { disconnect: team.tasks.map((t) => ({ id: t.id })) };
      }
      if (team.members?.length > 0) {
        teamData.members = { disconnect: team.members.map((m) => ({ id: m.id })) };
      }
      if (team.subscribers?.length > 0) {
        teamData.subscribers = { disconnect: team.subscribers.map((s) => ({ id: s.id })) };
      }
      if (team.subscribedTeams?.length > 0) {
        teamData.subscribedTeams = { disconnect: team.subscribedTeams.map((t) => ({ id: t.id })) };
      }
      if (team.subscriberTeams?.length > 0) {
        teamData.subscriberTeams = { disconnect: team.subscriberTeams.map((t) => ({ id: t.id })) };
      }
      await updateTeam({ variables: { data: teamData, where: { id: teamId } } });

      message.success('Team deleted successfully');
      setIsDeleteModalVisible(false);
      setDeleteConfirmText('');
      if (onChanged) onChanged();
      onClose();
    } catch (error) {
      message.error('Failed to delete team: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Members table columns
  const memberColumns = [
    {
      title: 'Member',
      key: 'member',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AvatarS3
            imgKey={record.avatar?.key}
            username={record.name || record.username}
            avatarProps={{ size: 32 }}
          />
          <div>
            <div style={{ fontWeight: 500, color: text.primary }}>
              {startCase(record.name || record.username)}
            </div>
            {record.username && record.name && (
              <div style={{ fontSize: 12, color: text.secondary }}>
                @{record.username}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      align: 'right',
      render: (_, record) => {
        const displayName = startCase(record.name || record.username);
        const userTaskCount = teamTasks.filter(
          (t) =>
            t.assigneeUsers?.some((u) => u.id === record.id) ||
            t.assigneeUser?.id === record.id
        ).length;
        const removeTitle =
          userTaskCount > 0
            ? `Remove ${displayName} from team? They will also be unassigned from ${userTaskCount} task${userTaskCount === 1 ? '' : 's'} in this team.`
            : `Remove ${displayName} from team?`;
        return (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <Popconfirm
              title={removeTitle}
              onConfirm={() => handleRemoveMember(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Remove from team">
                <Button type="text" icon={<UserDeleteOutlined />} danger size="small" />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Settings Modal */}
      <Modal
        className="task-create-modal"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: '#1890ff' }} />
            <span>Team Settings{team?.name ? ` — ${team.name}` : ''}</span>
          </div>
        }
        visible={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={700}
      >
        <Form form={settingsForm} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: 'Team name is required' },
              {
                validator: (_, value) => {
                  if (
                    value &&
                    (allTeams || []).some(
                      (t) => t.id !== teamId && t.name.toLowerCase() === value.trim().toLowerCase()
                    )
                  ) {
                    return Promise.reject(new Error('A team with this name already exists'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this team" maxLength={95} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={handleSettingsSave} loading={updateTeamLoading}>
              Save Details
            </Button>
          </div>
        </Form>

        {editingTeam && (
          <>
            {/* Members table */}
            <div style={{ marginBottom: space[3] }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[2] }}>
                <UserOutlined style={{ marginRight: 6 }} /> Members ({editingTeam.members?.length || 0})
              </div>

              <Table
                dataSource={editingTeam.members || []}
                columns={memberColumns}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{ emptyText: 'No members yet' }}
                footer={() => (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setSelectedMemberIds([]);
                      fetchUsersForOrg();
                      setIsAddMemberModalVisible(true);
                    }}
                    style={{ width: '100%' }}
                  >
                    Add Members
                  </Button>
                )}
              />
            </div>

            <Divider style={{ margin: `${space[4]}px 0 ${space[3]}px` }} />

            {/* Danger Zone */}
            <div
              style={{
                border: '1px solid #ffccc7',
                borderRadius: radius.md,
                padding: space[3],
                background: '#fff2f0',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#cf1322', marginBottom: space[1] }}>
                <DeleteOutlined style={{ marginRight: 6 }} /> Danger Zone
              </div>
              <div style={{ fontSize: 12, color: text.secondary, marginBottom: space[3] }}>
                Deleting this team will unassign all of its tasks, remove every member and subscriber, and disconnect all team-to-team subscriptions. This cannot be undone from the UI.
              </div>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setDeleteConfirmText('');
                  setIsDeleteModalVisible(true);
                }}
              >
                Delete Team
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* Add Members Modal */}
      <Modal
        className="task-create-modal"
        title="Add Members to Team"
        visible={isAddMemberModalVisible}
        onOk={handleAddMembers}
        onCancel={() => {
          setIsAddMemberModalVisible(false);
          setSelectedMemberIds([]);
        }}
        confirmLoading={updateTeamLoading}
        okText={`Add ${selectedMemberIds.length > 0 ? `(${selectedMemberIds.length})` : ''}`}
        width={500}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Search and select members to add"
          style={{ width: '100%' }}
          value={selectedMemberIds}
          onChange={setSelectedMemberIds}
          loading={basicUsersDetailsLoading}
          onSearch={(searchText) => fetchUsersForOrg(searchText)}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {basicUsersDetails
            .filter((user) => !editingTeam?.members?.some((m) => m.id === user.id))
            .map((user) => (
              <Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Option>
            ))}
        </Select>
      </Modal>

      {/* Delete Team Modal — typed confirmation */}
      <Modal
        className="task-create-modal"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cf1322' }}>
            <DeleteOutlined />
            <span>Delete Team</span>
          </div>
        }
        visible={isDeleteModalVisible}
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteModalVisible(false);
          setDeleteConfirmText('');
        }}
        maskClosable={!isDeleting}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsDeleteModalVisible(false);
              setDeleteConfirmText('');
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isDeleting}
            disabled={deleteConfirmText !== 'DELETE'}
            onClick={handleDeleteTeam}
          >
            Delete Team
          </Button>,
        ]}
        width={500}
      >
        <div style={{ marginBottom: space[3] }}>
          You are about to delete <strong>{team?.name}</strong>. This action will:
        </div>
        <ul style={{ marginBottom: space[3], color: text.secondary, paddingLeft: space[5] }}>
          <li>Unassign all tasks currently linked to this team</li>
          <li>Remove all {team?.members?.length || 0} member{(team?.members?.length || 0) === 1 ? '' : 's'} and any subscribers</li>
          <li>Disconnect all team-to-team subscriptions</li>
        </ul>
        <div style={{ marginBottom: space[2], fontSize: 13 }}>
          Type <strong>DELETE</strong> to confirm.
        </div>
        <Input
          placeholder="DELETE"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          onPressEnter={() => {
            if (deleteConfirmText === 'DELETE' && !isDeleting) handleDeleteTeam();
          }}
          autoFocus
          disabled={isDeleting}
        />
      </Modal>
    </>
  );
};

TeamSettingsModal.propTypes = {
  team: PropTypes.object,
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  allTeams: PropTypes.array,
};

TeamSettingsModal.defaultProps = {
  team: null,
  visible: false,
  onChanged: () => {},
  allTeams: [],
};

export default TeamSettingsModal;
